import React, { useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useGames, useUsers } from '@/hooks/useShared';
import { calculatePlayerDetail, calculatePlayerRank } from '@/services/insights';
import { calculateLeaderboard } from '@/services/leaderboard';
import { formatCurrency } from '@/lib/utils';
import { LoadingSpinner } from '@/components/ui';

export function PlayerDetailPage() {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const { games, loading: gamesLoading } = useGames();
  const { users, loading: usersLoading } = useUsers();

  const leaderboard = useMemo(() => calculateLeaderboard(users, games), [users, games]);
  const entry = useMemo(() => leaderboard.find(e => e.user_id === userId), [leaderboard, userId]);
  const detail = useMemo(() => calculatePlayerDetail(games, userId ?? ''), [games, userId]);
  const rank = useMemo(() => calculatePlayerRank(leaderboard, userId ?? ''), [leaderboard, userId]);

  if (gamesLoading || usersLoading) return <LoadingSpinner message="Loading player details..." />;
  if (!entry || !userId) {
    return (
      <div className="max-w-[400px] mx-auto">
        <div style={{ textAlign: 'center', color: '#6b7280', padding: 40, fontSize: 13 }}>Player not found.</div>
      </div>
    );
  }

  const winRate = entry.games_played > 0 ? Math.round((entry.wins / entry.games_played) * 100) : 0;

  return (
    <div className="max-w-[400px] mx-auto">
      {/* Back button */}
      <button
        onClick={() => navigate(-1)}
        style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 600, color: '#006855', padding: 0, marginBottom: 12, fontFamily: 'inherit' }}
      >
        ← Back
      </button>

      {/* Player header */}
      <div style={{ background: '#e8f5f0', border: '1px solid rgba(0,104,85,0.1)', borderRadius: 16, padding: 20, marginBottom: 12, textAlign: 'center' }}>
        <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '2px', color: 'rgba(0,104,85,0.7)', marginBottom: 4 }}>PLAYER STATS</div>
        <h1 style={{ fontSize: 22, fontWeight: 800, color: '#2c2f30', lineHeight: 1.2 }}>{entry.name}</h1>
        <div style={{ fontSize: 13, color: '#6b7280', marginTop: 4 }}>Rank #{rank} · {entry.games_played} games</div>
      </div>

      {/* Stat cards */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 16 }}>
        <MiniStat label="Games" value={String(entry.games_played)} />
        <MiniStat label="Wins" value={String(entry.wins)} color="#22c55e" />
        <MiniStat label="Losses" value={String(entry.losses)} color="#ef4444" />
        <MiniStat label="Win Rate" value={`${winRate}%`} />
        <MiniStat label="ROI" value={`${entry.roi >= 0 ? '+' : ''}${entry.roi}%`} color={entry.roi >= 0 ? '#22c55e' : '#ef4444'} />
      </div>

      {/* P&L */}
      <StatRow label="Total P&L" value={`${entry.total_pnl >= 0 ? '+' : ''}${formatCurrency(entry.total_pnl)}`} valueColor={entry.total_pnl >= 0 ? '#22c55e' : '#ef4444'} />

      {/* Streaks */}
      <Section title="Streaks">
        <div style={{ display: 'flex', gap: 6 }}>
          <StreakCard label="Longest Win" value={String(detail.longestWinStreak)} color="#22c55e" />
          <StreakCard label="Longest Loss" value={String(detail.longestLossStreak)} color="#ef4444" />
          <StreakCard
            label="Current"
            value={detail.currentStreak > 0 ? String(detail.currentStreak) : '—'}
            color={detail.currentStreakType === 'win' ? '#22c55e' : detail.currentStreakType === 'loss' ? '#ef4444' : '#6b7280'}
          />
        </div>
      </Section>

      {/* Best Win / Worst Loss */}
      <Section title="Best & Worst">
        <div style={{ display: 'flex', gap: 6 }}>
          <div style={{ flex: 1, background: 'white', borderRadius: 12, padding: 12, boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
            <div style={{ fontSize: 9, fontWeight: 700, color: '#22c55e', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 4 }}>Best Win</div>
            {detail.bestWin ? (
              <>
                <div style={{ fontSize: 16, fontWeight: 800, color: '#22c55e' }}>{formatCurrency(detail.bestWin.pnl)}</div>
                <div style={{ fontSize: 9, color: '#6b7280', marginTop: 2 }}>{detail.bestWin.date} · {detail.bestWin.venue}</div>
              </>
            ) : (
              <div style={{ fontSize: 12, color: '#6b7280' }}>No wins</div>
            )}
          </div>
          <div style={{ flex: 1, background: 'white', borderRadius: 12, padding: 12, boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
            <div style={{ fontSize: 9, fontWeight: 700, color: '#ef4444', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 4 }}>Worst Loss</div>
            {detail.worstLoss ? (
              <>
                <div style={{ fontSize: 16, fontWeight: 800, color: '#ef4444' }}>{formatCurrency(detail.worstLoss.pnl)}</div>
                <div style={{ fontSize: 9, color: '#6b7280', marginTop: 2 }}>{detail.worstLoss.date} · {detail.worstLoss.venue}</div>
              </>
            ) : (
              <div style={{ fontSize: 12, color: '#6b7280' }}>No losses</div>
            )}
          </div>
        </div>
      </Section>

      {/* P&L Timeline */}
      <Section title="P&L Timeline">
        <div style={{ background: 'white', borderRadius: 12, padding: 12, boxShadow: '0 1px 3px rgba(0,0,0,0.05)', maxHeight: 260, overflowY: 'auto' }}>
          {detail.timeline.length === 0 ? (
            <div style={{ textAlign: 'center', color: '#6b7280', padding: 20, fontSize: 12 }}>No game history.</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              {detail.timeline.map((t, i) => (
                <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '4px 0', borderBottom: i < detail.timeline.length - 1 ? '1px solid #f0f0f0' : 'none' }}>
                  <div>
                    <span style={{ fontSize: 10, fontWeight: 600, color: '#006855' }}>{t.date}</span>
                    <span style={{ fontSize: 9, color: '#6b7280', marginLeft: 6 }}>{t.venue}</span>
                  </div>
                  <span style={{ fontSize: 12, fontWeight: 700, color: t.pnl >= 0 ? '#22c55e' : '#ef4444' }}>
                    {t.pnl >= 0 ? '+' : ''}{formatCurrency(t.pnl)}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </Section>

      {/* Monthly Breakdown */}
      <Section title="Monthly">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {detail.monthlyBreakdown.length === 0 ? (
            <div style={{ textAlign: 'center', color: '#6b7280', padding: 20, fontSize: 12 }}>No data.</div>
          ) : (
            detail.monthlyBreakdown.map(m => (
              <div key={m.month} style={{ background: 'white', borderRadius: 12, padding: '10px 12px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <div style={{ fontSize: 11, fontWeight: 700, color: '#2c2f30' }}>{m.month}</div>
                  <div style={{ fontSize: 9, color: '#6b7280' }}>{m.gameCount} games</div>
                </div>
                <span style={{ fontSize: 13, fontWeight: 700, color: m.totalPnl >= 0 ? '#22c55e' : '#ef4444' }}>
                  {m.totalPnl >= 0 ? '+' : ''}{formatCurrency(m.totalPnl)}
                </span>
              </div>
            ))
          )}
        </div>
      </Section>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div style={{ marginBottom: 16 }}>
      <h2 style={{ fontSize: 11, fontWeight: 700, color: '#006855', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: 8 }}>{title}</h2>
      {children}
    </div>
  );
}

function MiniStat({ label, value, color }: { label: string; value: string; color?: string }) {
  return (
    <div style={{ flex: 1, background: 'white', borderRadius: 10, padding: '8px 4px', textAlign: 'center', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
      <div style={{ fontSize: 8, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{label}</div>
      <div style={{ fontSize: 13, fontWeight: 800, color: color ?? '#2c2f30', marginTop: 2 }}>{value}</div>
    </div>
  );
}

function StatRow({ label, value, valueColor }: { label: string; value: string; valueColor: string }) {
  return (
    <div style={{ background: 'white', borderRadius: 12, padding: '10px 12px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
      <span style={{ fontSize: 11, fontWeight: 700, color: '#2c2f30' }}>{label}</span>
      <span style={{ fontSize: 16, fontWeight: 800, color: valueColor }}>{value}</span>
    </div>
  );
}

function StreakCard({ label, value, color }: { label: string; value: string; color: string }) {
  return (
    <div style={{ flex: 1, background: 'white', borderRadius: 10, padding: '10px 8px', textAlign: 'center', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
      <div style={{ fontSize: 8, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 2 }}>{label}</div>
      <div style={{ fontSize: 18, fontWeight: 800, color }}>{value}</div>
    </div>
  );
}
